import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import wishlistService from '../services/wishlistService';
import { AuthContext } from './AuthContext'; // Import AuthContext directly
import usePushNotifications from '../hooks/usePushNotifications';
import { trackAddToWishlist } from '../utils/marketingAnalytics';

const WishlistContext = createContext();

export const useWishlist = () => useContext(WishlistContext);

const GUEST_WISHLIST_KEY = 'guestWishlist';

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]); // Stores product IDs or full items
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { authState } = useContext(AuthContext); // Use useContext(AuthContext)
  const { user, isAuthenticated, loading: authLoading } = authState; // Destructure from authState
  const { deviceToken } = usePushNotifications(); // Get device token for guest wishlists

  // --- LocalStorage Helpers ---
  const getGuestWishlist = useCallback(() => {
    try {
      const storedWishlist = localStorage.getItem(GUEST_WISHLIST_KEY);
      return storedWishlist ? JSON.parse(storedWishlist) : [];
    } catch (parseError) {
      console.error("Error parsing guest wishlist from localStorage:", parseError);
      localStorage.removeItem(GUEST_WISHLIST_KEY); // Clear corrupted data
      return [];
    }
  }, []);

  const updateGuestWishlist = useCallback((items) => {
    try {
      localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(items));
      setWishlistItems(items); // Update state for guests
    } catch (storageError) {
      console.error("Error saving guest wishlist to localStorage:", storageError);
      setError("Could not update wishlist locally.");
    }
  }, []);

  // --- Core Logic ---

  // Function to load wishlist based on auth status
  const loadWishlist = useCallback(async () => {
    if (authLoading) return; // Wait for auth status to be determined

    setLoading(true);
    setError(null);
    try {
      if (isAuthenticated && user) {
        // Logged-in user: Fetch from API
        const apiItems = await wishlistService.fetchWishlist();
        setWishlistItems(apiItems || []);
      } else {
        // Guest: Fetch from API (will use deviceToken or sessionId automatically)
        // The wishlistService will handle fallback to sessionId if no deviceToken
        const guestItems = await wishlistService.fetchWishlist(deviceToken);
        setWishlistItems(guestItems || []);
      }
    } catch (err) {
      console.error("Failed to load wishlist:", err);
      setError("Could not load wishlist.");
      setWishlistItems([]); // Reset on error
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, authLoading, deviceToken]);

  // Function to add an item
  const addItem = useCallback(async (productId) => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    try {
      if (isAuthenticated && user) {
        // Logged-in: Call API without device token
        await wishlistService.addItemToWishlist(productId);
        await loadWishlist();
      } else {
        // Guest: Call API (will use deviceToken or sessionId automatically)
        await wishlistService.addItemToWishlist(productId, deviceToken);
        await loadWishlist();
      }
      // Meta audience: content_ids only (context only has productId)
      trackAddToWishlist({ product_id: productId });
    } catch (err) {
      console.error("Failed to add item to wishlist:", err);
      setError("Could not add item to wishlist.");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, deviceToken, loadWishlist]);

  // Function to remove an item
  const removeItem = useCallback(async (productId) => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    try {
      if (isAuthenticated && user) {
        // Logged-in: Call API without device token
        await wishlistService.removeItemFromWishlist(productId);
        await loadWishlist();
      } else {
        // Guest: Call API (will use deviceToken or sessionId automatically)
        await wishlistService.removeItemFromWishlist(productId, deviceToken);
        await loadWishlist();
      }
    } catch (err) {
      console.error("Failed to remove item from wishlist:", err);
      setError("Could not remove item from wishlist.");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, deviceToken, loadWishlist]);

  // Function to check if an item is in the wishlist
  const isInWishlist = useCallback((productId) => {
    if (!productId) return false;

    // Normalize the productId for comparison
    const numericId = parseInt(productId, 10);
    const stringId = String(productId);

    // Both guests and authenticated users now have objects with productId
    return wishlistItems.some(item => {
      // Handle both object and primitive item structures
      if (typeof item === 'object' && item !== null) {
        // Check multiple possible ID properties to handle different data structures
        const itemId = item.productId || item.id ||
                     (item.product && (item.product.product_id || item.product.id));

        return itemId === numericId || itemId === stringId;
      } else {
        // Handle primitive values (direct ID comparison) - legacy fallback
        return item === numericId || item === stringId;
      }
    });
  }, [wishlistItems]);


  // --- Effects ---

  // Load wishlist on initial mount and when auth status changes
  useEffect(() => {
    loadWishlist();
  }, [loadWishlist]); // Dependency array includes loadWishlist which depends on auth state

  // Merge guest wishlist to backend on login
  useEffect(() => {
    const mergeGuestWishlist = async () => {
      if (isAuthenticated && user && deviceToken) {
        setLoading(true);
        try {
          // Call the backend merge endpoint with device token
          const result = await wishlistService.mergeGuestWishlist(deviceToken);
          console.log(`✅ Wishlist merge complete: ${result.merged} items merged, ${result.skipped} skipped`);

          // Clear localStorage backup (if any)
          localStorage.removeItem(GUEST_WISHLIST_KEY);

          // Reload wishlist from backend
          await loadWishlist();
        } catch (mergeError) {
          console.error("Error during guest wishlist merge:", mergeError);
          setError("Could not fully merge guest wishlist.");
          // Still reload to show current state
          await loadWishlist();
        } finally {
          setLoading(false);
        }
      }
    };

    mergeGuestWishlist();
  }, [isAuthenticated, user, deviceToken, loadWishlist]); // Runs when auth state changes


  // Clear wishlist on logout
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      // User has logged out or session expired, clear API-based items
      // Guest items remain in localStorage unless explicitly cleared elsewhere
      setWishlistItems(getGuestWishlist()); // Show guest list immediately if they log out
    }
  }, [isAuthenticated, authLoading, getGuestWishlist]);


  const contextValue = {
    wishlistItems,
    loading,
    error,
    loadWishlist,
    addItem,
    removeItem,
    isInWishlist,
    itemCount: wishlistItems.length, // Simple count based on current state
  };

  return (
    <WishlistContext.Provider value={contextValue}>
      {children}
    </WishlistContext.Provider>
  );
};